import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaBuilding, FaUser, FaPercent, FaEuroSign, FaExclamationTriangle } from 'react-icons/fa';

const SplitConfiguration = ({ 
  activity, 
  onSplitChange, 
  availablePayers = [],
  className = "" 
}) => {
  const [splits, setSplits] = useState([
    { 
      id: Date.now(),
      payer_id: activity.patient_id, 
      payer_type: 'User', 
      percent: 100, 
      max_amount: null 
    }
  ]);
  const [totalPercent, setTotalPercent] = useState(100);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Calcular total de porcentajes y validar
  useEffect(() => {
    const total = splits.reduce((sum, split) => sum + (parseFloat(split.percent) || 0), 0);
    const errors = [];
    
    // Validar porcentajes
    const percentValid = Math.abs(total - 100) <= 0.01;
    if (!percentValid) {
      errors.push(`Los porcentajes suman ${total.toFixed(2)}% (debe ser 100%)`);
    }
    
    // Validar que todos los splits tienen payer_id válido
    const emptySplits = splits.filter(split => !split.payer_id || split.payer_id.trim() === '');
    if (emptySplits.length > 0) {
      errors.push(`${emptySplits.length} pagador(es) sin seleccionar`);
    }
    
    // Validar que no hay porcentajes negativos o > 100
    const invalidPercents = splits.filter(split => split.percent < 0 || split.percent > 100);
    if (invalidPercents.length > 0) {
      errors.push(`Porcentajes inválidos detectados`);
    }
    
    setTotalPercent(total);
    setValidationErrors(errors);
    setIsValid(errors.length === 0);
  }, [splits]);

  // Notificar cambios al componente padre
  useEffect(() => {
    console.log(`SplitConfiguration: Notificando cambios para actividad ${activity.key}`);
    console.log('Splits actuales:', splits);
    console.log('Es válido:', isValid);
    console.log('Errores de validación:', validationErrors);
    
    if (onSplitChange) {
      onSplitChange(activity.key, splits, isValid);
    }
  }, [splits, isValid, activity.key, onSplitChange]);

  // Inicialización - asegurar que siempre hay un split del paciente por defecto
  useEffect(() => {
    console.log('SplitConfiguration inicializado para actividad:', activity.key);
    console.log('Paciente:', activity.patient_id, activity.patient_name);
    console.log('Available payers:', availablePayers);
    
    // Verificar que el split inicial del paciente es válido
    if (splits.length === 1 && splits[0].payer_id === activity.patient_id) {
      console.log('Split inicial del paciente ya configurado correctamente');
    } else {
      console.log('Configurando split inicial del paciente');
      const initialSplit = {
        id: Date.now(),
        payer_id: activity.patient_id,
        payer_type: 'User',
        percent: 100,
        max_amount: null
      };
      setSplits([initialSplit]);
    }
  }, []);

  // Añadir nuevo split
  const addSplit = () => {
    console.log('Añadiendo nuevo split');
    
    const newSplits = [...splits];
    
    // Redistribuir porcentajes proporcionalmente
    if (totalPercent >= 100 && newSplits.length > 0) {
      const reductionPerSplit = 100 / (newSplits.length + 1);
      
      newSplits.forEach((split, index) => {
        newSplits[index] = { ...split, percent: Math.round(reductionPerSplit * 100) / 100 };
      });
    }
    
    const totalAfterReduction = newSplits.reduce((sum, s) => sum + s.percent, 0);
    const remainingPercent = Math.max(0, 100 - totalAfterReduction);
    
    const newSplit = {
      id: Date.now(),
      payer_id: '', // IMPORTANTE: Inicialmente vacío, el usuario debe seleccionar
      payer_type: 'Company',
      percent: remainingPercent > 0 ? Math.round(remainingPercent * 100) / 100 : Math.round((100 / (newSplits.length + 1)) * 100) / 100,
      max_amount: null
    };
    
    const finalSplits = [...newSplits, newSplit];
    console.log('Splits después de añadir:', finalSplits);
    
    setSplits(finalSplits);
  };

  // Actualizar split específico
  const updateSplit = (index, field, value) => {
    console.log(`Actualizando split ${index}, campo: ${field}, valor:`, value);
    
    const newSplits = [...splits];
    newSplits[index] = { ...newSplits[index], [field]: value };
    
    // Si se cambia el tipo de pagador, limpiar el payer_id
    if (field === 'payer_type') {
      newSplits[index].payer_id = '';
    }
    
    console.log('Splits después de actualizar:', newSplits);
    setSplits(newSplits);
  };

  // Actualizar porcentaje con validación
  const handlePercentChange = (index, value) => {
    const percent = Math.max(0, Math.min(100, parseFloat(value) || 0));
    updateSplit(index, 'percent', percent);
  };

  // Remover split (no permitir si es el último o el del paciente principal)
  const removeSplit = (index) => {
    if (splits.length > 1) {
      const splitToRemove = splits[index];
      
      // No permitir eliminar el split del paciente si es el único de tipo User
      const userSplits = splits.filter(s => s.payer_type === 'User');
      if (splitToRemove.payer_type === 'User' && userSplits.length === 1) {
        alert('Debe mantener al menos un split del paciente');
        return;
      }
      
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  // Distribuir porcentajes uniformemente
  const distributeEvenly = () => {
    const percentPerSplit = Math.round((100 / splits.length) * 100) / 100;
    let remainder = 100 - (percentPerSplit * splits.length);
    
    const newSplits = splits.map((split, index) => ({
      ...split,
      percent: index === 0 ? percentPerSplit + remainder : percentPerSplit
    }));
    
    setSplits(newSplits);
  };

  // Obtener opciones de pagador según tipo
  const getPayerOptions = (currentPayerType) => {
    return availablePayers.filter(payer => 
      payer.type === currentPayerType || 
      (currentPayerType === 'User' && payer.type === 'patient')
    );
  };

  // Obtener nombre del pagador
  const getPayerName = (payerId, payerType) => {
    if (!payerId) return 'Seleccionar...';
    
    const payer = availablePayers.find(p => p.id === payerId);
    if (payer) {
      return payer.name || payer.display_name || payer.full_name;
    }
    if (payerId === activity.patient_id) {
      return activity.patient_name || 'Paciente';
    }
    return 'Desconocido';
  };

  // Calcular importe para un porcentaje dado
  const calculateAmount = (percent) => {
    const total = activity.total_amount || 0;
    return (total * percent / 100).toFixed(2);
  };

  // Validar split individual
  const validateSplit = (split, index) => {
    const errors = [];
    
    if (!split.payer_id || split.payer_id.trim() === '') {
      errors.push('Debe seleccionar un pagador');
    }
    
    if (split.percent <= 0) {
      errors.push('El porcentaje debe ser mayor a 0');
    }
    
    if (split.percent > 100) {
      errors.push('El porcentaje no puede exceder 100%');
    }
    
    return errors;
  };

  return (
    <div className={`split-configuration ${className}`}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-1">
            <FaPercent className="me-2" />
            Configuración de Pagadores
          </h6>
          <small className="text-muted">
            {activity?.description} - Total: €{activity?.total_amount?.toFixed(2) || '0.00'}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={distributeEvenly}
            title="Distribuir uniformemente"
            disabled={splits.length === 0}
          >
            Distribuir Uniformemente
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={addSplit}
            title="Agregar Pagador"
            disabled={splits.length >= 5}
          >
            <FaPlus className="me-1" />
            Agregar Pagador
          </button>
        </div>
      </div>

      {/* Lista de splits */}
      <div className="splits-list">
        {splits.map((split, index) => {
          const splitErrors = validateSplit(split, index);
          const isPatientSplit = split.payer_id === activity.patient_id && split.payer_type === 'User';
          const hasErrors = splitErrors.length > 0;
          
          return (
            <div key={split.id || index} className={`card mb-2 ${hasErrors ? 'border-warning' : ''}`}>
              <div className="card-body p-3">
                <div className="row g-2 align-items-center">
                  {/* Tipo de pagador */}
                  <div className="col-md-3">
                    <label className="form-label small">Tipo</label>
                    <select
                      className="form-select form-select-sm"
                      value={split.payer_type}
                      onChange={(e) => updateSplit(index, 'payer_type', e.target.value)}
                      disabled={isPatientSplit}
                    >
                      <option value="User">
                        Paciente
                      </option>
                      <option value="Company">
                        Aseguradora/Empresa
                      </option>
                      <option value="HealthCenter">
                        Centro Médico
                      </option>
                    </select>
                  </div>

                  {/* Selección de pagador */}
                  <div className="col-md-3">
                    <label className="form-label small">Pagador</label>
                    {isPatientSplit ? (
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        value={activity.patient_name || 'Paciente'}
                        readOnly
                      />
                    ) : (
                      <select
                        className={`form-select form-select-sm ${!split.payer_id ? 'border-warning' : ''}`}
                        value={split.payer_id}
                        onChange={(e) => updateSplit(index, 'payer_id', e.target.value)}
                      >
                        <option value="">-- Seleccionar pagador --</option>
                        {getPayerOptions(split.payer_type).map(payer => (
                          <option key={payer.id} value={payer.id}>
                            {payer.name || payer.display_name}
                            {payer.cif && ` (${payer.cif})`}
                          </option>
                        ))}
                      </select>
                    )}
                    
                    {/* Mostrar errores del split */}
                    {splitErrors.length > 0 && (
                      <div className="text-warning small mt-1">
                        {splitErrors.map((error, i) => (
                          <div key={i}>
                            <FaExclamationTriangle className="me-1" />
                            {error}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Porcentaje */}
                  <div className="col-md-2">
                    <label className="form-label small">% Copago</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="number"
                        className={`form-control ${split.percent <= 0 || split.percent > 100 ? 'border-warning' : ''}`}
                        value={split.percent}
                        onChange={(e) => handlePercentChange(index, e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>

                  {/* Importe calculado */}
                  <div className="col-md-2">
                    <label className="form-label small">Importe</label>
                    <div className="form-control form-control-sm bg-light text-end">
                      €{calculateAmount(split.percent)}
                    </div>
                  </div>

                  {/* Tope máximo opcional */}
                  <div className="col-md-1">
                    <label className="form-label small">Tope €</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={split.max_amount || ''}
                      onChange={(e) => updateSplit(index, 'max_amount', e.target.value ? parseFloat(e.target.value) : null)}
                      min="0"
                      step="0.01"
                      placeholder="Sin tope"
                    />
                  </div>

                  {/* Acciones */}
                  <div className="col-md-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeSplit(index)}
                      disabled={splits.length <= 1 || isPatientSplit}
                      title={isPatientSplit ? 'No se puede eliminar el split del paciente' : 'Eliminar pagador'}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {/* Información adicional del split */}
                {split.max_amount && parseFloat(calculateAmount(split.percent)) > split.max_amount && (
                  <div className="alert alert-info alert-sm mt-2 mb-0">
                    <strong>Tope aplicado:</strong> €{split.max_amount} 
                    (calculado: €{calculateAmount(split.percent)})
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Resumen y validación total */}
      <div className="validation-summary">
        <div className={`card ${isValid ? 'border-success' : 'border-danger'}`}>
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong className={isValid ? 'text-success' : 'text-danger'}>
                  Total porcentajes: {totalPercent.toFixed(2)}%
                </strong>
                
                {/* Mostrar errores de validación */}
                {validationErrors.length > 0 && (
                  <div className="text-danger small mt-1">
                    {validationErrors.map((error, index) => (
                      <div key={index}>
                        <FaExclamationTriangle className="me-1" />
                        {error}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Estado de validación */}
                <div className="small mt-1">
                  {isValid ? (
                    <span className="text-success">
                      ✓ Configuración válida
                    </span>
                  ) : (
                    <span className="text-danger">
                      ✗ Configuración incompleta
                    </span>
                  )}
                </div>
              </div>
              
              <div className="text-end">
                <div className="text-muted small">Total a facturar:</div>
                <strong className="text-success">
                  €{activity?.total_amount?.toFixed(2) || '0.00'}
                </strong>
                
                {/* Distribución por pagador */}
                {isValid && (
                  <div className="mt-1">
                    {splits.filter(split => split.payer_id).map((split, idx) => (
                      <div key={split.id || idx} className="text-xs text-muted">
                        {getPayerName(split.payer_id, split.payer_type)}: 
                        €{calculateAmount(split.percent)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Debug info (removable in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-2">
          <small className="text-muted">
            Debug - Activity Key: {activity.key} | Valid: {isValid ? 'Yes' : 'No'} | Splits: {splits.length}
          </small>
        </div>
      )}
    </div>
  );
};

export default SplitConfiguration;